import { Injectable, inject } from '@angular/core';
import { Role } from '@curbnturf/entities';
import { AuthService } from './auth.service';
import { HostListingsService } from '../host/host-listings.service';

/**
 * Single place that decides where a user lands right after they authenticate
 * (sign-in, sign-up, or a federated callback) — role selection, "how did you
 * hear about us" attribution, and the role-specific first-run wizard, in that
 * order, re-checked on every sign-in until each is actually done. Kept as its
 * own service (not a method on AuthService) because HostListingsService
 * already injects AuthService — adding the reverse dependency there would be
 * circular.
 */
/** Long enough for a slow phone on hotel wifi, short enough that nobody thinks
 *  the button is broken. */
const REFRESH_TIMEOUT_MS = 4000;

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    work.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

@Injectable({ providedIn: 'root' })
export class PostAuthRedirectService {
  private auth = inject(AuthService);
  private hostListings = inject(HostListingsService);

  async resolve(returnTo?: string | null): Promise<string> {
    const fallback = returnTo || '/user-dashboard/dashboard';
    const user = await this.auth.currentUserOnceLoaded();
    if (!user) return fallback;

    if (!user.role) return this.withReturnTo('/welcome', returnTo);
    if (!user.source) return this.withReturnTo('/welcome/source', returnTo);

    if (user.role === Role.host) {
      // Never let this call decide *whether* the visitor moves at all: it is a
      // network round trip, and a slow or failing one used to leave them
      // staring at the login form they had just successfully submitted. On
      // timeout or error we fall through to whatever we already know.
      try {
        await withTimeout(this.hostListings.refreshNow(), REFRESH_TIMEOUT_MS);
      } catch {
        return fallback;
      }
      if (!this.hostListings.hasAnyListings()) return '/properties/new';
      return fallback;
    }

    if (user.role === Role.guest) {
      if (!user.phone || !user.photoUrl) return this.withReturnTo('/welcome/guest', returnTo);
      return fallback;
    }

    return fallback;
  }

  private withReturnTo(path: string, returnTo?: string | null): string {
    return returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;
  }
}
